import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Spin, Button, Modal, Tooltip, message } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, CheckCircleOutlined } from "@ant-design/icons";
import TransactionCreationForm from "../../Forms/TransactionCreationForm/TransactionCreationForm";
import { updateTransaction } from "../../../../Services/TransactionService";
import { createStock, getConsignmentType } from "../../../../Services/StockService";

const SpecializedTransactionTable = ({
  history,
  loading,
  data,
  callReFetch,
  dispatch,
  handleDelete,
  discountData,
  modalForm,
  userData,
  pagination
}) => {

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Control who can adjust via explicit allowlist of usernames
  const allowedAdjustUsers = ["leo",'Yonathan','Mary','MaryMRI','MerryMRI','Merry','Lidya','Siti','Rusmono','RusmonoMri','Angga']; // TODO: maintain this list as needed
  const canAdjust = allowedAdjustUsers.includes(userData?.username);

  // Detect window size for mobile view
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleOpenEditModal = (id) => {
    const transactionToEdit = data.find(
      (transaction) => transaction._id === id
    );
    if (transactionToEdit) {
      setSelectedTransaction(transactionToEdit);
      setIsEditModalVisible(true);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setSelectedTransaction(null);
  };

  const handleAdjustStock = (id) => {
    // Find the transaction data
    const transaction = data.find(t => t._id === id);
    if (!transaction) {
      message.error("Transaction not found.");
      return;
    }

    // Execute immediately without confirmation
    executeAdjustStock(id, transaction);
  };

  const executeAdjustStock = async (id, transaction) => {
    try {

      // Extract size from last 2 digits of barcode
      const barcodeSize = transaction.barcode ? 
        parseInt(transaction.barcode.slice(-2), 10) || 0 : 0;

      // Determine consignmentType based on existing stock
      const consignmentType = await deriveConsignmentType(
        transaction.barcode,
        transaction.counterCode
      );

      // Create stock entry with the transaction quantity
      const stockData = {
        noSJ: "ADJ-" + transaction.counterCode + '-' + new Date().toISOString().slice(0, 10) + '-' + Date.now(),
        article: transaction.article || "",
        brand: transaction.brand || "", // From transaction or empty string
        color: transaction.color || "",
        size: barcodeSize, // Extract from last 2 digits of barcode
        quantity: transaction.quantity,
        assort: "1", // Default assort value for single item
        price: transaction.price || 0,
        barcode: transaction.barcode,
        counterCode: transaction.counterCode,
        currentQuantity: transaction.quantity,
        receivedQuantity: transaction.quantity,
        status: "in stock",
        consignmentType,
        ...(transaction.sku && { sku: transaction.sku }) // Include SKU if it exists
      };

      console.log(stockData);
      // Create the stock first
      const stockResponse = await createStock(stockData);
      
      if (!stockResponse || !stockResponse.data) {
        message.error("Failed to create stock entry. Please try again.");
        return;
      }

      // Then update the transaction to set isBermasalah to false
      const transactionResponse = await updateTransaction(id, { isBermasalah: false });
      
      if (transactionResponse && transactionResponse.data) {
        message.success(`Stock created successfully (${transaction.quantity} units). Transaction is no longer problematic.`);
        // Refresh the data to reflect the changes
        callReFetch();
      } else {
        message.error("Stock created but failed to update transaction status. Please try again.");
      }
    } catch (error) {
      console.error("Error adjusting stock:", error);
      message.error(error?.response?.data?.message || "Failed to adjust stock. Please try again.");
    }
  };

  const deriveConsignmentType = async (barcode, counterCode) => {
    try {
      const resp = await getConsignmentType(barcode, counterCode);
      const type = resp?.data?.consignmentType;
      return type || "putus";
    } catch (e) {
      return "putus";
    }
  };

  const handleMarkAsResolved = async (id) => {
    try {
      const transactionResponse = await updateTransaction(id, { isBermasalah: false });
      
      if (transactionResponse && transactionResponse.data) {
        message.success("Transaction marked as resolved.");
        callReFetch();
      } else {
        message.error("Failed to update transaction status. Please try again.");
      }
    } catch (error) {
      console.error("Error marking transaction as resolved:", error);
      message.error(error?.response?.data?.message || "Failed to mark transaction as resolved. Please try again.");
    }
  };

  const renderActionButton = (id) => {
    return (
      <>
        <Tooltip title="Adjust Stock">
          <Button
            icon={<PlusOutlined />}
            onClick={() => handleAdjustStock(id)}
            shape="circle"
            type="primary"
            style={{ marginRight: 8 }}
          />
        </Tooltip>

        <Tooltip title="Mark as Resolved">
          <Button
            icon={<CheckCircleOutlined />}
            onClick={() => handleMarkAsResolved(id)}
            shape="circle"
            type="default"
          />
        </Tooltip>
      </>
    );
  };

  const handleTableOnChange = (pagination, filteredInfo, sorter) => {
    dispatch({ type: "sorter", payload: sorter });
    dispatch({ type: "filteredInfo", payload: filteredInfo });
    callReFetch();
  };

  const baseColumns = [
    {
      title: "Transaction Date",
      dataIndex: "transactionDate",
      key: "transactionDate",
      sorter: true,
      render: (value) => (value ? new Date(value).toLocaleDateString() : "N/A"),
    },
    {
      title: "Counter Name",
      dataIndex: "counterName",
      key: "counterName",
    },
    {
      title: "Department",
      dataIndex: "counterDept",
      key: "counterDept",
    },
    { title: "No Bon", dataIndex: "noBon", key: "noBon", sorter: true },
    { title: "Barcode", dataIndex: "barcode", key: "barcode", sorter: true },
    { title: "Article", dataIndex: "article", key: "article", sorter: true },
    { title: "Color", dataIndex: "color", key: "color", sorter: true },
    { title: "Quantity", dataIndex: "quantity", key: "quantity", sorter: true },
    {
      title: "Problematic Type",
      dataIndex: "bR",
      key: "bR",
      render: (bR) => {
        if (bR === 1) return "Stock < 0";
        if (bR === 2) return "Transaction w/o Stock";
        return "-";
      },
    },
  ];

  // Add Actions column only for allowed users
  const columns = canAdjust 
    ? [
        ...baseColumns,
        {
          title: "Actions",
          key: "actions",
          render: (_, record) => renderActionButton(record._id),
          width: 180,
          fixed: "right",
        },
      ]
    : baseColumns;

    // const columns = baseColumns;

  return (
    <div style={{ width: "80vw" }}>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={data}
          onChange={handleTableOnChange}
          rowKey="_id"
          pagination={pagination || { pageSize: 10 }}
          scroll={{ x: "max-content" }} // Enables horizontal scrolling if needed
        />
      </Spin>
      <Modal
        title="Edit Transaction"
        open={isEditModalVisible}
        onCancel={handleCloseEditModal}
        footer={null}
      >
        {selectedTransaction && (
          <TransactionCreationForm
            transactionData={selectedTransaction}
            onClose={handleCloseEditModal}
            callReFetch={callReFetch}
            discountData={discountData}
            modalForm={modalForm}
            callReFetchTransaction={callReFetch}
            setIsEditModalVisible={setIsEditModalVisible}
            isEditMode
          />
        )}
      </Modal>
    </div>
  );
};

SpecializedTransactionTable.propTypes = {
  history: PropTypes.object,
  loading: PropTypes.bool,
  data: PropTypes.array,
  callReFetch: PropTypes.func,
  dispatch: PropTypes.func,
  handleDelete: PropTypes.func,
  pagination: PropTypes.object,
  discountData: PropTypes.array,
  modalForm: PropTypes.object,
  userData: PropTypes.object
};

export default SpecializedTransactionTable;

